/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-08-12 00:00:00
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { useEffect, useState, useCallback } from 'react';
import { Stack, Thumbnail, Caption, DropZone, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { MAX_FILE_SIZE } from 'src/constants/theme-settings';

const CustomDropZoneStandard = (props: any | WithI18nProps) => {
  const limitSize = props.limitSize || MAX_FILE_SIZE;
  const allowMultiple = props.allowMultiple || false;
  const disabled = props.disabled || false;
  const type = 'file'; // Default file
  const accept = props.accept || 'text/csv'; // Default import csv
  const validFileTypes = props.fileTypes; // Viewed file in thumbnail

  const [files, setFiles] = useState([]);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const { i18n, defaultFile, defaultFileSize } = props;

  useEffect(() => {
    return () => {
      props.onUnmount && props.onUnmount();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (defaultFile && _.isObject(defaultFile)) {
      // Render when change default file image
      setFiles([defaultFile]);
      checkValidFileSize(defaultFile);
    } else if (_.isString(defaultFile)) {
      // Render exist URL string image from DB
      setFiles([]);
      props.onError && props.onError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFile, props.name]);

  const handleDropZoneDrop = useCallback(
    (dropFiles, _acceptedFiles, _rejectedFiles) => {
      if (_.isEmpty(_acceptedFiles)) {
        return;
      }

      const newFiles = props.allowMultiple ? [...files, ..._acceptedFiles] : [_acceptedFiles[0]];
      setFiles([_acceptedFiles[0]]);

      for (let file of _acceptedFiles) {
        const isValid = checkValidFileSize(file);
        if (!isValid) {
          break;
        }
      }

      setFiles(files => newFiles);
      props.onSelectFile(newFiles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props, files]
  );

  const toggleOpenFileDialog = useCallback(() => {
    setOpenFileDialog(openFileDialog => !openFileDialog);
  }, []);

  const displayFileName = (fileName: string) => {
    if (_.isEmpty(fileName) || fileName.length < 20) {
      return fileName;
    }

    return `${fileName.substring(0, 8)}...${fileName.substring(fileName.length - 10)}`;
  };

  const checkValidFileSize = file => {
    let isValidFileSize = true;
    if (file.size > limitSize) {
      isValidFileSize = false;
    }

    if (isValidFileSize) {
      props.onError && props.onError('');
    } else {
      props.onError &&
        props.onError(
          !allowMultiple ? i18n.translate('AdminHTML.ThemeSetting.Common.limitFileSizeUpload') : ''
        );
    }
    return isValidFileSize;
  };

  let fileUpload = '';
  if (!files.length) {
    if (_.isEmpty(defaultFile)) {
      fileUpload = (
        <div style={{ padding: '0.625rem', textAlign: 'center' }}>
          <Stack distribution="center">
            <Stack vertical>
              <TextStyle variation="subdued">
                {i18n.translate('AdminHTML.Common.CustomDropzone.dropzoneStandardContent1')}
              </TextStyle>
              <TextStyle variation="subdued">
                {i18n.translate('AdminHTML.Common.CustomDropzone.dropzoneStandardContent2')}
              </TextStyle>
            </Stack>
          </Stack>
        </div>
      );
    } else {
      const name = defaultFile.split('/').pop();
      fileUpload = (
        <Stack alignment="center">
          <Thumbnail size="small" alt={name} source={defaultFile} />
          <div className="file-info">
            {displayFileName(name)}
            {defaultFileSize && <Caption>{defaultFileSize} bytes</Caption>}
          </div>
        </Stack>
      );
    }
  }

  const uploadedFiles = files.length > 0 && (
    <Stack vertical>
      {files.map((file, index) => (
        <Stack alignment="center" key={index}>
          <Thumbnail
            size="small"
            alt={file.name}
            source={
              validFileTypes.includes(file.type)
                ? window.URL.createObjectURL(file)
                : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
            }
          />
          <div className="file-info">
            {displayFileName(file.name)} <Caption>{file.size} bytes</Caption>
          </div>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <div className="BC-ThemeSettings_Dropzone">
      <div className={`custom-dropzone-standard-v2 ${files.length > 0 ? 'uploaded' : ''}`}>
        <DropZone
          disabled={disabled}
          accept={accept}
          type={type}
          openFileDialog={openFileDialog}
          onDrop={handleDropZoneDrop}
          onClick={toggleOpenFileDialog}
          onDragLeave={() => {}}
          allowMultiple={allowMultiple}
          variableHeight>
          {uploadedFiles}
          {fileUpload}
        </DropZone>
      </div>
    </div>
  );
};

export default withI18n()(CustomDropZoneStandard);
